import { useTranslation } from '@pulsex/localization'
import { Currency, CurrencyAmount, Percent, TradeType } from '@pulsex/sdk'
import { SmartRouter, SmartRouterTrade } from '@pulsex/smart-router'
import { AutoColumn, BackForwardIcon, Button, Flex, QuestionHelper, Text } from '@pulsex/uikit'
import { formatAmount } from '@pulsex/utils/formatFractions'
import { CurrencyLogo } from '@pulsex/widgets-internal'
import { AutoRow, RowBetween, RowFixed } from 'components/Layout/Row'
import { ReactElement, memo, useMemo, useState } from 'react'
import { Field } from 'state/swap/actions'
import { styled } from 'styled-components'
import { basisPointsToPercent, warningSeverity } from 'utils/exchange'

import FormattedPriceImpact from './FormattedPriceImpact'
import { StyledBalanceMaxMini, SwapCallbackError } from './styleds'
import { SlippageAdjustedAmounts, formatExecutionPrice } from '../utils/exchange'

const SwapModalFooterContainer = styled(AutoColumn)`
  background-color: ${({ theme }) => theme.colors.backgroundAlt2};
  padding: 16px;
  border-radius: ${({ theme }) => theme.radii.default};
  margin-top: 24px;
`

export const SwapModalFooter = memo(function SwapModalFooter({
  priceImpact: priceImpactWithoutFee,
  lpFee: realizedLPFee,
  inputAmount,
  outputAmount,
  trade,
  tradeType,
  slippageAdjustedAmounts,
  isEnoughInputBalance,
  onConfirm,
  swapErrorMessage,
  disabledConfirm,
  currencyBalances,
  allowedSlippage,
}: {
  trade?: Pick<SmartRouterTrade<TradeType>, 'inputAmount' | 'outputAmount'>
  tradeType: TradeType
  lpFee?: CurrencyAmount<Currency>
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
  priceImpact?: Percent
  slippageAdjustedAmounts: SlippageAdjustedAmounts | undefined | null
  isEnoughInputBalance?: boolean
  swapErrorMessage?: string | undefined
  disabledConfirm: boolean
  isRFQReady?: boolean
  currencyBalances?: {
    INPUT?: CurrencyAmount<Currency>
    OUTPUT?: CurrencyAmount<Currency>
  }
  onConfirm: () => void
  allowedSlippage: number | ReactElement
}) {
  const { t } = useTranslation()
  const [showInverted, setShowInverted] = useState<boolean>(false)
  const severity = warningSeverity(priceImpactWithoutFee)

  const executionPriceDisplay = useMemo(() => {
    const price = SmartRouter.getExecutionPrice(trade) ?? undefined
    return formatExecutionPrice(price, inputAmount, outputAmount, showInverted)
  }, [inputAmount, outputAmount, trade, showInverted])

  const amount =
    tradeType === TradeType.EXACT_INPUT
      ? formatAmount(slippageAdjustedAmounts?.[Field.OUTPUT], 6)
      : formatAmount(slippageAdjustedAmounts?.[Field.INPUT], 6)
  const symbol = tradeType === TradeType.EXACT_INPUT ? outputAmount.currency.symbol : inputAmount.currency.symbol

  const tradeInfoText = useMemo(() => {
    return tradeType === TradeType.EXACT_INPUT
      ? t('Output is estimated. You will receive at least %amount% %symbol% or the transaction will revert.', {
        amount: `${amount}`,
        symbol,
      })
      : t('Input is estimated. You will sell at most %amount% %symbol% or the transaction will revert.', {
        amount: `${amount}`,
        symbol,
      })
  }, [t, tradeType, amount, symbol])

  return (
    <>
      <SwapModalFooterContainer>
        <RowBetween align="center" mb="8px">
          <Text fontSize="14px">{t('Price')}</Text>
          <Text
            fontSize="14px"
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              textAlign: 'right',
              paddingLeft: '10px',
            }}
          >
            {executionPriceDisplay}
            <StyledBalanceMaxMini onClick={() => setShowInverted(!showInverted)}>
              <BackForwardIcon color="textSubtle" width="14px" />
            </StyledBalanceMaxMini>
          </Text>
        </RowBetween>
        <RowBetween mb="8px">
          <RowFixed>
            <Text fontSize="14px">{t('Slippage tolerance')}</Text>
            <QuestionHelper
              text={tradeInfoText}
              ml="4px"
              placement="top"
            />
          </RowFixed>
          <RowFixed>
            <Text fontSize="14px" bold ml="auto" textAlign="end">
              {typeof allowedSlippage === 'number'
                ? `${basisPointsToPercent(allowedSlippage).toFixed(2)}%`
                : allowedSlippage}
            </Text>
          </RowFixed>
        </RowBetween>
        <RowBetween mb="8px">
          <RowFixed>
            <Text fontSize="14px">
              {tradeType === TradeType.EXACT_INPUT ? t('Minimum received') : t('Maximum sold')}
            </Text>
            <QuestionHelper
              text={t(
                'Your transaction will revert if there is a large, unfavorable price movement before it is confirmed.',
              )}
              ml="4px"
              placement="top"
            />
          </RowFixed>
          <RowFixed>
            <Text fontSize="14px">
              {tradeType === TradeType.EXACT_INPUT
                ? formatAmount(slippageAdjustedAmounts?.[Field.OUTPUT], 4) ?? '-'
                : formatAmount(slippageAdjustedAmounts?.[Field.INPUT], 4) ?? '-'}
            </Text>
            <Text fontSize="14px" marginLeft="4px">
              {tradeType === TradeType.EXACT_INPUT ? outputAmount.currency.symbol : inputAmount.currency.symbol}
            </Text>
          </RowFixed>
        </RowBetween>
        <RowBetween mb="8px">
          <RowFixed>
            <Text fontSize="14px">{t('Price Impact')}</Text>
            <QuestionHelper
              ml="4px"
              placement="top"
              text={<>{t('The difference between the market price and your price due to trade size.')}</>}
            />
          </RowFixed>
          <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
        </RowBetween>
        <RowBetween>
          <RowFixed>
            <Text fontSize="14px">{t('Trading Fee')}</Text>
          </RowFixed>
          <RowFixed>
            {realizedLPFee ? (
              <Flex justifyContent="center" alignItems="center">
                <Text fontSize="14px" mr="8px">
                  {`${formatAmount(realizedLPFee, 6)} ${inputAmount.currency.symbol}`}
                </Text>
                <CurrencyLogo currency={currencyBalances?.INPUT?.currency ?? inputAmount.currency} size="24px" />
              </Flex>
            ) : (
              <Text fontSize="14px" textAlign="right">
                -
              </Text>
            )}
          </RowFixed>
        </RowBetween>
      </SwapModalFooterContainer>

      <AutoRow>
        <Button
          variant={severity > 2 ? 'danger' : 'primary'}
          onClick={onConfirm}
          disabled={disabledConfirm}
          mt="12px"
          id="confirm-swap-or-send"
          width="100%"
        >
          {severity > 2 || (tradeType === TradeType.EXACT_OUTPUT && !isEnoughInputBalance) ? (
            t('Swap Anyway')
          ) : (
            t('Confirm Swap')
          )}
        </Button>

        {swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
      </AutoRow>
    </>
  )
})
